import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, Grid, MenuItem, Paper, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { exportCSVFromDataGrid } from 'src/utils/exportCSV';
import { WorkPanel } from './WorkPanel';
import { DueCell } from './DueCell';
import { WORK_STATES, WORK_STATE_COLORS, exportableColumns, formatCycle, nextItem, previousItem, queueQuery, queueReason, withView, workStateLabel } from './queue';
import { answered } from 'src/components/property/UtilityPortalCredentials/asyncResult';
import { isLivableStaffFromToken } from 'src/utils/isLivableStaffFromToken';
const EMPTY = {
  items: [],
  total: 0,
  outstanding: 0,
  unclaimed: 0,
  claimed: 0,
  manualTotal: 0,
  automatedTotal: 0,
  overdue: 0,
  late: 0
};

/**
 * The billing team's work queue: every allocation still waiting on a bill.
 *
 * Rendered as a tab on the admin page and also mounted at /bill-queue, which the Livy palette links
 * to. Box rather than Container for that reason: nested inside the admin page's own Container the
 * grid would be constrained to the narrower default width and sit inset from the tab row above it.
 * Completion is derived, never stored twice.
 */
export const BillQueuePage = observer(() => {
  var _page$late;
  const store = useStore();
  const _useState = useState(EMPTY),
    _useState2 = _slicedToArray(_useState, 2),
    page = _useState2[0],
    setPage = _useState2[1];
  const _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    cycles = _useState4[0],
    setCycles = _useState4[1];
  const _useState5 = useState({
      loading: true,
      error: '',
      toast: ''
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    state = _useState6[0],
    setState = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    selectedId = _useState8[0],
    setSelectedId = _useState8[1];
  // `run()` cancels the in-flight request every time it is called and the model swallows the
  // cancel, so a stale response must not be allowed to paint over a newer one.
  const requestSeq = useRef(0);
  const _useState9 = useState({
      cycleId: '',
      // Failed plus manual: the rows a person has to act on. Rows the daily collection will fill stay out of view.
      needsPerson: true,
      onlyManual: false,
      onlyUnclaimed: false,
      automationFailed: false,
      late: false,
      mine: false,
      workState: '',
      includeDeadEnds: false,
      search: ''
    }),
    _useState0 = _slicedToArray(_useState9, 2),
    filters = _useState0[0],
    setFilters = _useState0[1];

  // From the token, not the store view: the localStorage mirror is written after the first render
  // on a cold load and is not observable, so the gate settled on "not staff" and stayed there.
  const isStaff = isLivableStaffFromToken();

  // Debounced separately from the rest of the filters: every keystroke used to refire a 500-row
  // cross-company query, and each cancelled response resolved undefined, which rendered as
  // "Outstanding 0" between characters.
  const _useState1 = useState(''),
    _useState10 = _slicedToArray(_useState1, 2),
    debouncedSearch = _useState10[0],
    setDebouncedSearch = _useState10[1];
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);
  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setState(s => _objectSpread(_objectSpread({}, s), {}, {
      loading: true,
      error: ''
    }));
    try {
      const resp = await store.admin.getBillQueueAsync.run(queueQuery(filters, debouncedSearch));
      if (seq !== requestSeq.current) return; // superseded; the newer request owns the state
      const data = (resp === null || resp === void 0 ? void 0 : resp.response) || resp;
      // The model resolves undefined on failure rather than rejecting, so an empty queue and a
      // failed request are indistinguishable without this. "No outstanding bills" is the one
      // message an operator must never be shown wrongly - it reads as "no work", and the queue
      // that fed it had simply died (the current cycle's read times out at the gateway's 29s cap).
      if (!answered(resp) || store.admin.getBillQueueAsync.isError || !Array.isArray(data === null || data === void 0 ? void 0 : data.items)) {
        setPage(EMPTY);
        setState(s => _objectSpread(_objectSpread({}, s), {}, {
          loading: false,
          error: 'Could not load the bill queue.'
        }));
        return;
      }
      setPage(data);
      setState(s => _objectSpread(_objectSpread({}, s), {}, {
        loading: false
      }));
    } catch (ex) {
      if (seq !== requestSeq.current) return;
      setPage(EMPTY);
      setState(s => _objectSpread(_objectSpread({}, s), {}, {
        loading: false,
        error: 'Could not load the bill queue.'
      }));
    }
    // filters.search is deliberately absent: the debounced copy drives the query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, debouncedSearch, filters.cycleId, filters.needsPerson, filters.onlyManual, filters.onlyUnclaimed, filters.automationFailed, filters.late, filters.mine, filters.workState, filters.includeDeadEnds]);
  useEffect(() => {
    if (!isStaff) return undefined;
    load();
    return undefined;
  }, [isStaff, load]);
  useEffect(() => {
    if (!isStaff) return undefined;
    let cancelled = false;
    store.admin.getBillQueueCyclesAsync.run().then(resp => {
      const data = (resp === null || resp === void 0 ? void 0 : resp.response) || resp || [];
      const list = Array.isArray(data) ? data : [];
      if (cancelled) return;
      setCycles(list);
      // Default to the newest cycle, not every open one. Ordering by cut-off is right, but with
      // "all cycles" it surfaces years-old abandoned rows above this month's work. Rollover
      // hunting across cycles stays one click away rather than being the daily default.
      setFilters(f => f.cycleId || !list.length ? f : _objectSpread(_objectSpread({}, f), {}, {
        cycleId: list[0].id
      }));
    }).catch(() => {
      if (!cancelled) setCycles([]);
    });
    return () => {
      cancelled = true;
    };
  }, [store, isStaff]);
  const selected = useMemo(() => page.items.find(i => i.allocationTableId === selectedId) || null, [page.items, selectedId]);

  /**
   * Runs an async model action and reports what actually happened.
   *
   * `createAsync().run()` never rejects: asyncModel catches the error, records it on the model and
   * resolves `undefined`. So `await run(...)` followed by a success toast reports success on
   * failure, and a `try/catch` around it never fires. Every write on this page said it had worked
   * whether or not it had.
   */
  const runAndReport = async (asyncModel, action, successToast, failureToast) => {
    await action();
    const failed = asyncModel.isError;
    setState(s => _objectSpread(_objectSpread({}, s), {}, {
      toast: failed ? failureToast : successToast
    }));
    await load();
    return !failed;
  };
  const handleClaim = item => runAndReport(store.admin.claimBillQueueItemAsync, () => store.admin.claimBillQueueItemAsync.run({
    allocationTableId: item.allocationTableId
  }), 'Claimed.',
  // A 409 is the normal outcome of two people clicking at once, not a fault to shout about.
  'Someone else claimed this first.');
  const handleRelease = item => runAndReport(store.admin.releaseBillQueueItemAsync, () => store.admin.releaseBillQueueItemAsync.run(item.allocationTableId), 'Released.', 'Could not release this row.');
  const handleSetState = (item, workState, note) => runAndReport(store.admin.setBillQueueStateAsync, () => store.admin.setBillQueueStateAsync.run({
    allocationTableId: item.allocationTableId,
    workState,
    note
  }), "Marked ".concat(workStateLabel(workState).toLowerCase(), "."), 'Could not update this row.');
  const handleEnterBill = async (item, entry) => {
    var _nextItem$allocationT, _nextItem;
    const saved = await store.admin.enterBillQueueBillAsync.run(_objectSpread({
      allocationTableId: item.allocationTableId
    }, entry));

    // createAsync().run() does NOT reject on an API failure: asyncModel catches, records the error
    // on the model, and returns undefined. Awaiting it and then reporting success unconditionally
    // told an operator "Bill saved" when nothing had been saved, on the money path. Throw so the
    // drawer's own error handling runs and the row stays open.
    if (store.admin.enterBillQueueBillAsync.isError || !saved) {
      var _store$admin$enterBil;
      // Deliberately does not claim the bill was not saved. CreateBillAsync commits before the
      // allocation is triggered, so a failure after that point leaves a real bill behind: observed
      // on a local rig where the publish failed and the Bill row was already written. Telling an
      // operator "not saved" would send them to enter it a second time.
      // errorResp is { title, body: [...] }, so passing it straight to Error() stringified it as
      // [object Object] and the drawer fell back to the generic message for every rejection -
      // including the fixable ones like a duplicate bill or an out-of-cycle period.
      const failure = (_store$admin$enterBil = store.admin.enterBillQueueBillAsync.error) === null || _store$admin$enterBil === void 0 ? void 0 : _store$admin$enterBil.errorResp;
      throw new Error([failure === null || failure === void 0 ? void 0 : failure.title, ...((failure === null || failure === void 0 ? void 0 : failure.body) || [])].filter(Boolean).join(' ') || 'Could not confirm the bill. Close this and reopen the row before trying again.');
    }
    setState(s => _objectSpread(_objectSpread({}, s), {}, {
      toast: 'Bill saved. Sent to QA for review.'
    }));
    // The row leaves the queue once it has a bill, so advance before reloading.
    setSelectedId((_nextItem$allocationT = (_nextItem = nextItem(page.items, item.allocationTableId)) === null || _nextItem === void 0 ? void 0 : _nextItem.allocationTableId) !== null && _nextItem$allocationT !== void 0 ? _nextItem$allocationT : null);
    await load();
  };
  const columns = useMemo(() => [{
    field: 'propertyAddress',
    headerName: 'Property',
    flex: 1.4,
    minWidth: 220,
    isExportable: true,
    renderCell: _ref => {
      let row = _ref.row;
      return /*#__PURE__*/React.createElement(Stack, {
        sx: {
          paddingY: '6px'
        }
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body2"
      }, row.propertyAddress || row.propertyName), /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        color: "text.secondary"
      }, row.companyName));
    }
  }, {
    field: 'utilityTypeName',
    headerName: 'Utility',
    width: 120,
    isExportable: true
  }, {
    field: 'providerName',
    headerName: 'Provider',
    flex: 1,
    minWidth: 170,
    isExportable: true,
    renderCell: _ref2 => {
      let row = _ref2.row;
      return /*#__PURE__*/React.createElement(Stack, {
        sx: {
          paddingY: '6px'
        }
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body2"
      }, row.providerName), /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        color: queueReason(row).color
      }, queueReason(row).label));
    }
  }, {
    field: 'expectedBillDate',
    headerName: 'Expected',
    width: 220,
    isExportable: true,
    renderCell: _ref3 => {
      let row = _ref3.row;
      return /*#__PURE__*/React.createElement(DueCell, {
        row: row
      });
    },
    valueGetterForExport: _ref4 => {
      let row = _ref4.row;
      return row.expectedBillDate || 'unknown';
    }
  }, {
    field: 'cycle',
    headerName: 'Cycle',
    width: 110,
    isExportable: true,
    renderCell: _ref5 => {
      let row = _ref5.row;
      return formatCycle(row.year, row.month);
    },
    valueGetterForExport: _ref6 => {
      let row = _ref6.row;
      return formatCycle(row.year, row.month);
    }
  }, {
    field: 'workState',
    headerName: 'State',
    width: 160,
    isExportable: true,
    renderCell: _ref7 => {
      let row = _ref7.row;
      return /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        label: workStateLabel(row.workState),
        color: WORK_STATE_COLORS[row.workState] || 'default'
      });
    },
    valueGetterForExport: _ref8 => {
      let row = _ref8.row;
      return workStateLabel(row.workState);
    }
  }, {
    field: 'assignedToName',
    headerName: 'Who',
    width: 150,
    isExportable: true,
    renderCell: _ref9 => {
      let row = _ref9.row;
      return row.assignedToName || '-';
    },
    valueGetterForExport: _ref0 => {
      let row = _ref0.row;
      return row.assignedToName || '-';
    }
  }, {
    field: 'actions',
    headerName: '',
    width: 110,
    sortable: false,
    // No isExportable, and neither has any credential column: a CSV of this queue with logins
    // in it would be the tracker spreadsheet again.
    renderCell: _ref1 => {
      let row = _ref1.row;
      return /*#__PURE__*/React.createElement(Button, {
        size: "small",
        variant: "actionButton",
        onClick: () => setSelectedId(row.allocationTableId)
      }, "Work it");
    }
  }], []);
  if (!isStaff) {
    return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Alert, {
      severity: "error",
      sx: {
        marginTop: '24px'
      }
    }, "This page is for Livable staff."));
  }
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      paddingY: '24px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, "Bill processing queue"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      marginY: '8px'
    },
    color: "text.secondary"
  }, "Every utility still waiting on a bill. Claim a row so nobody duplicates it, open the portal from here, and enter the bill. Passwords are copied to the clipboard, never displayed, and every copy is audited."), state.error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error"
  }, state.error, " Counts and the queue are hidden until a load succeeds.") : null, state.error ? null : /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 2,
    sx: {
      marginY: '8px'
    }
  }, [{
    label: 'Outstanding',
    value: page.outstanding
  }, {
    label: 'Overdue',
    value: page.overdue
  }, {
    label: 'Late',
    value: (_page$late = page.late) !== null && _page$late !== void 0 ? _page$late : 0
  }, {
    label: 'Unclaimed',
    value: page.unclaimed
  }, {
    label: 'Manual',
    value: page.manualTotal
  }].map(tile => /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 6,
    md: true,
    key: tile.label
  }, /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, tile.label), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, tile.value))))), state.error ? null : /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 2,
    sx: {
      marginBottom: '16px'
    },
    flexWrap: "wrap",
    useFlexGap: true,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(TextField, {
    select: true,
    size: "small",
    label: "Cycle",
    value: filters.cycleId,
    onChange: e => setFilters(f => _objectSpread(_objectSpread({}, f), {}, {
      cycleId: e.target.value
    })),
    sx: {
      minWidth: 180
    }
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ""
  }, "All open cycles"), cycles.map(cycle => /*#__PURE__*/React.createElement(MenuItem, {
    key: cycle.id,
    value: cycle.id
  }, "".concat(formatCycle(cycle.year, cycle.month), " (").concat(cycle.outstanding, ")")))), /*#__PURE__*/React.createElement(TextField, {
    select: true,
    size: "small",
    label: "State",
    value: filters.workState,
    onChange: e => setFilters(f => _objectSpread(_objectSpread({}, f), {}, {
      workState: e.target.value
    })),
    sx: {
      minWidth: 180
    }
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ""
  }, "Any open state"), Object.entries(WORK_STATES).map(_ref10 => {
    let _ref11 = _slicedToArray(_ref10, 2),
      value = _ref11[0],
      label = _ref11[1];
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: value,
      value: value
    }, label);
  })), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      size: "small",
      checked: filters.includeDeadEnds,
      onChange: e => setFilters(f => _objectSpread(_objectSpread({}, f), {}, {
        includeDeadEnds: e.target.checked
      }))
    }),
    label: "Show closed"
  }), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    label: "Search address, provider, account",
    value: filters.search,
    onChange: e => setFilters(f => _objectSpread(_objectSpread({}, f), {}, {
      search: e.target.value
    })),
    sx: {
      minWidth: 260
    }
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: filters.needsPerson,
      onChange: e => setFilters(f => withView(f, 'needsPerson', e.target.checked))
    }),
    label: "Needs a person"
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: filters.onlyManual,
      onChange: e => setFilters(f => withView(f, 'onlyManual', e.target.checked))
    }),
    label: "Manual only"
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: filters.automationFailed,
      onChange: e => setFilters(f => withView(f, 'automationFailed', e.target.checked))
    }),
    label: "Automation failed"
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: filters.late,
      onChange: e => setFilters(f => withView(f, 'late', e.target.checked))
    }),
    label: "Late"
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: filters.onlyUnclaimed,
      onChange: e => setFilters(f => _objectSpread(_objectSpread({}, f), {}, {
        onlyUnclaimed: e.target.checked
      }))
    }),
    label: "Unclaimed"
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: filters.mine,
      onChange: e => setFilters(f => _objectSpread(_objectSpread({}, f), {}, {
        mine: e.target.checked
      }))
    }),
    label: "Mine"
  }), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flexGrow: 1
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => exportCSVFromDataGrid({
      columns: exportableColumns(columns),
      rows: page.items,
      name: 'bill-queue.csv'
    })
  }, "Export CSV")), state.loading ? /*#__PURE__*/React.createElement(Box, {
    sx: {
      padding: '32px',
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(CircularProgress, null)) : /*#__PURE__*/React.createElement(DataGrid, {
    autoHeight: true,
    columns: columns,
    rows: page.items,
    getRowId: row => row.allocationTableId,
    getRowHeight: () => 'auto',
    disableRowSelectionOnClick: true,
    onRowDoubleClick: _ref12 => {
      let row = _ref12.row;
      return setSelectedId(row.allocationTableId);
    },
    initialState: {
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 25
        }
      }
    },
    pageSizeOptions: [25, 50, 100]
  })), /*#__PURE__*/React.createElement(WorkPanel, {
    item: selected,
    open: !!selected,
    onClose: () => setSelectedId(null),
    onClaim: handleClaim,
    onRelease: handleRelease,
    onSetState: handleSetState,
    onEnterBill: handleEnterBill,
    onPrevious: () => {
      var _previousItem$allocat, _previousItem;
      return setSelectedId((_previousItem$allocat = (_previousItem = previousItem(page.items, selectedId)) === null || _previousItem === void 0 ? void 0 : _previousItem.allocationTableId) !== null && _previousItem$allocat !== void 0 ? _previousItem$allocat : selectedId);
    },
    onNext: () => {
      var _nextItem$allocationT2, _nextItem2;
      return setSelectedId((_nextItem$allocationT2 = (_nextItem2 = nextItem(page.items, selectedId)) === null || _nextItem2 === void 0 ? void 0 : _nextItem2.allocationTableId) !== null && _nextItem$allocationT2 !== void 0 ? _nextItem$allocationT2 : selectedId);
    }
  }), /*#__PURE__*/React.createElement(Snackbar, {
    open: !!state.toast,
    autoHideDuration: 3000,
    onClose: () => setState(s => _objectSpread(_objectSpread({}, s), {}, {
      toast: ''
    })),
    message: state.toast
  }));
});